import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { AnimatePresence, MotionConfig, motion, useReducedMotion } from 'motion/react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import {
  generationKeys,
  isGenerationActive,
  listGenerationSummaries,
  readGenerationsSeen,
  removeGeneration,
  useGenerationsPolling,
  useGenerationsQuery,
  useGenerationWatch,
  writeGenerationsSeen,
  type GenerationPrefill,
  type GenerationSummary,
} from '@entities/Generation';
import { useQuotaQuery } from '@entities/Plan';
import { useAuthStore } from '@entities/User';
import { trackEvent } from '@shared/analytics';
import { cn } from '@shared/lib';
import { ChevronDownIcon } from '@shared/ui';
import { loadUntil } from './lib/loadMore';
import { rowEnter } from './lib/rowEnter';
import { isAboveView, onScrollSettled, SCROLL_SETTLE_MS, scrollRoot, scrollToTop, spaceBelow } from './lib/scroll';
import { useNow } from './model/useNow';
import { useRowsInView } from './model/useRowsInView';
import { GenerationRow, headerDomId } from './ui/GenerationRow';
import type { ItemActionsContext } from './ui/ItemLine';
import { Bar, Spinner } from './ui/SenseLine';
import './GenerationsList.css';

/** Видно первые 5; «Показать ещё» добавляет 10; страница списка — 10 (ТЗ главной § 22.7). */
const WINDOW_BASE = 5;
const WINDOW_STEP = 10;
/** Раскрыли запись, а под заголовком до строки видно меньше — заголовок встаёт наверх (§ 22.5). */
const MIN_SPACE_BELOW = 160;

export interface GenerationsSectionProps {
  /** Телефонная раскладка — только для `surface` событий § 17: размеры виджет берёт из CSS. */
  isMobile: boolean;
  /** Корень липкой полосы создания: её высота вычитается из видимой области (§ 8). */
  dockRef: RefObject<HTMLElement>;
  /** «Ввести снова» и «Добавить вручную»: страница передаёт это строке создания пропом `prefill`. */
  onReenter: (prefill: GenerationPrefill) => void;
  /** Строка создания заблокирована (почта, доступ): этих двух кнопок в записях нет. */
  composerLocked: boolean;
  /** visit — только записи нажатий этого захода, ещё идущие и `keepIds`, без «Показать ещё», заголовок «Сегодня»:
   *  на «Словах и модулях» историю заменил список слов (вариант B, 2026-10-28). */
  scope?: 'all' | 'visit';
  /** При `scope="visit"` — ещё и эти записи: сегодняшние, где осталось что тренировать (с сервера). Так «Сегодня»
   *  не пустеет после обновления страницы (владелец 2026-10-28). */
  keepIds?: readonly string[];
  /** При `scope="visit"` — только записи этого модуля: ниже — слова выбранного модуля, и «Тренировать новые» тоже
   *  о нём (2026-10-28). */
  moduleId?: string | null;
  /** Справа от заголовка «Сегодня» — общее действие страницы («Тренировать новые»). */
  headerAction?: ReactNode;
}

type ExpandMode = 'animated' | 'instant';

/** Нажатия этого захода по записям: id → когда нажали (отсчёт «дольше обычного», § 22.4). */
function pressedThisVisit(state: { visit: number; entries: Record<string, { visit: number; startedAt: number }> }) {
  const out: Record<string, number> = {};
  for (const [id, entry] of Object.entries(state.entries)) if (entry.visit === state.visit) out[id] = entry.startedAt;
  return out;
}

/**
 * Секция «Недавно добавленные» под модулями (ТЗ главной § 22; в коде — «Генерации»): записи нажатий одним
 * блоком с разделителями, первые 5 и «Показать ещё», раскрытие на месте разрезом. Смонтирована на главной
 * всегда и сама решает, что рисовать (§ 23.4, п. 3): пусто — ничего; загрузка и ошибка — только если на
 * этом устройстве записи уже были (`lx.generations.seen.<userId>`, § 22.8). Здесь же запрос списка,
 * запасной опрос и наблюдение «запись на экране» для сигнала у строки (§ 8); нажатие сигнала приходит
 * в сторе слежения (`revealRequest`). Строки — `memo` со стабильными обработчиками: событие о записи
 * перерисовывает только её строку, а не весь список с раскрытыми разрезами.
 */
export function GenerationsSection({
  isMobile,
  dockRef,
  onReenter,
  composerLocked,
  scope = 'all',
  keepIds,
  moduleId,
  headerAction,
}: GenerationsSectionProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const titleId = useId();
  const userId = useAuthStore((s) => s.user?.id);
  const surface = isMobile ? 'phone' : 'desktop';
  const reduced = useReducedMotion() ?? false;
  const now = useNow();
  const query = useGenerationsQuery();
  useGenerationsPolling();
  // Из слежения — только нужное: нажатия этого захода и состав отслеживаемых (для момента вставки).
  const pressed = useGenerationWatch(useShallow(pressedThisVisit));
  const watchedKey = useGenerationWatch((s) => Object.keys(s.entries).join('|'));
  const visitOnly = scope === 'visit';
  const allItems = useMemo(() => listGenerationSummaries(query.data), [query.data]);
  const keep = useMemo(() => new Set(keepIds ?? []), [keepIds]);
  const items = useMemo(
    () =>
      visitOnly
        ? allItems.filter(
            (s) =>
              (pressed[s.id] !== undefined || isGenerationActive(s) || keep.has(s.id)) &&
              (!moduleId || !s.module || s.module.id === moduleId),
          )
        : allItems,
    [allItems, visitOnly, pressed, keep, moduleId],
  );
  // Сегодняшняя запись дальше загруженных страниц (много нажатий за день) — дочитать список до неё, но не глубже
  // суток: запись удалённого модуля в список не попадёт, и без предела читалась бы вся история.
  const last = allItems[allItems.length - 1];
  const keepMissing =
    visitOnly &&
    [...keep].some((id) => !allItems.some((s) => s.id === id)) &&
    !!last &&
    Date.now() - Date.parse(last.createdAt) < 24 * 60 * 60 * 1000;
  useEffect(() => {
    if (keepMissing && query.hasNextPage && !query.isFetchingNextPage && !query.isError) void query.fetchNextPage();
  }, [keepMissing, query]);
  const hasItems = items.length > 0;

  // Жетоны — для кнопок записи (§ 22.6): дневные и несгораемые вместе.
  const quota = useQuotaQuery().data;
  const quotaOut = !!quota && quota.remaining + quota.bonus <= 0;
  const ctx = useMemo<ItemActionsContext>(
    () => ({ surface, composerLocked, quotaOut, onReenter }),
    [surface, composerLocked, quotaOut, onReenter],
  );

  // --- Признак «записи уже были на этом устройстве» (§ 22.8, § 23.4 п. 3): ставим, когда в кэше списка
  // есть запись (ответ сервера или вставка после «Создать»), снимаем при пустом ответе сервера.
  const [seen, setSeen] = useState(() => readGenerationsSeen(userId));
  useEffect(() => setSeen(readGenerationsSeen(userId)), [userId]);
  // По всему списку, а не по окну захода: признак говорит, были ли записи у ученика вообще.
  const hasAny = allItems.length > 0;
  const emptyResponse = query.isSuccess && !query.isFetching && !hasAny;
  useEffect(() => {
    if (hasAny) {
      writeGenerationsSeen(userId, true);
      setSeen(true);
    } else if (emptyResponse) {
      writeGenerationsSeen(userId, false);
      setSeen(false);
    }
  }, [hasAny, emptyResponse, userId]);

  // --- Список перечитан в этом заходе: с этого момента строкам разрешена вспышка «готово» (§ 22.4). Смена,
  // которую принёс сам первый ответ после возврата на главную, — не то, что строка «увидела».
  const [listSettled, setListSettled] = useState(false);
  useEffect(() => {
    if (!listSettled && !query.isFetching && (query.isFetchedAfterMount || query.isError)) setListSettled(true);
  }, [listSettled, query.isFetching, query.isFetchedAfterMount, query.isError]);

  // --- Окно списка.
  const [limit, setLimit] = useState(WINDOW_BASE);
  const [moreState, setMoreState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [instantExit, setInstantExit] = useState(false);
  // Записей одного захода немного — видны все, без «Показать ещё».
  const visible = visitOnly ? items : items.slice(0, limit);
  const canShowMore = !visitOnly && (items.length > limit || !!query.hasNextPage);
  const canCollapse = !visitOnly && !canShowMore && limit > WINDOW_BASE && items.length > WINDOW_BASE;
  // Как появилась строка — по позиции и прошлому рендеру (`rowEnter`): новая запись сверху въезжает по
  // высоте, открытые «Показать ещё» — проявляются вместе. Первая отрисовка — без анимации.
  const knownIds = useRef<Set<string> | null>(null);
  const previousLimit = useRef(limit);
  useEffect(() => {
    knownIds.current = new Set(items.map((s) => s.id));
    previousLimit.current = limit;
  });

  // --- Раскрытые записи (можно несколько; после перезагрузки все свёрнуты).
  const [expanded, setExpanded] = useState<Record<string, ExpandMode>>({});
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const [highlight, setHighlight] = useState<{ id: string; key: number } | null>(null);
  const [leaving, setLeaving] = useState<Record<string, true>>({});

  // --- «Запись на экране» и момент вставки — для сигнала у строки (§ 8). Момент вставки — когда строка доехала
  // (впервые получила полную высоту заголовка, `useRowsInView`), а не когда она появилась в DOM: на первом кадре
  // въезжающая строка высотой 0 — «не на экране», и при редких кадрах решение через 300 мс от появления успевало
  // раньше следующего кадра (A2-01). Пишется и тогда, когда нажатие попало в слежение позже, чем его строка доехала
  // (SSE раньше ответа, повтор `replayed`): иначе сигнал по нему ждал бы запасной срок.
  const arrived = useRef(new Set<string>());
  const onArrived = useCallback((id: string) => {
    arrived.current.add(id);
    useGenerationWatch.getState().markInserted([id]);
  }, []);
  const rows = useRowsInView(dockRef, onArrived);
  const visibleKey = visible.map((s) => s.id).join('|');
  useEffect(() => {
    const store = useGenerationWatch.getState();
    const ids = visible.map((s) => s.id);
    // Строка уже в окне, пусть и въезжает: запасной срок «строки нет» к ней не применяется — ждём, пока доедет.
    if (ids.length) store.markMounted(ids);
    const done = ids.filter((id) => arrived.current.has(id));
    if (done.length) store.markInserted(done);
    // По составу окна и слежения.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey, watchedKey]);

  // --- «Дольше обычного» (§ 22.4): от нажатия этого захода, иначе — с первого показа «генерируется».
  const firstSeen = useRef(new Map<string, number>());
  const firstSeenAt = useCallback((key: string) => {
    let at = firstSeen.current.get(key);
    if (at === undefined) {
      at = Date.now();
      firstSeen.current.set(key, at);
    }
    return at;
  }, []);

  const headingRef = useRef<HTMLHeadingElement>(null);
  // Обработчики строк стабильны: читают актуальные значения через ref.
  const live = useRef({ surface, reduced, dockHeightNow: rows.dockHeightNow });
  live.current = { surface, reduced, dockHeightNow: rows.dockHeightNow };
  const root = useCallback(() => scrollRoot(dockRef.current), [dockRef]);

  const toggle = useCallback((summary: GenerationSummary) => {
    const opening = !expandedRef.current[summary.id];
    setExpanded((prev) => {
      const next = { ...prev };
      if (next[summary.id]) delete next[summary.id];
      else next[summary.id] = 'animated';
      return next;
    });
    if (opening) {
      trackEvent('generation_expanded', { surface: live.current.surface, source: 'list', kind: summary.kind, status: summary.status });
    }
  }, []);

  /** «Свернуть» внизу записи: заголовок за верхом окна — сначала прокрутка к нему, потом сворачивание. */
  const collapseFromBottom = useCallback(
    (id: string) => {
      const main = root();
      const header = document.getElementById(headerDomId(id));
      const close = () =>
        setExpanded((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      const instant = live.current.reduced;
      if (main && header && isAboveView(main, header)) {
        scrollToTop(main, header, instant);
        window.setTimeout(close, instant ? 0 : SCROLL_SETTLE_MS);
      } else close();
    },
    [root],
  );

  /** Раскрытие доехало, а под заголовком видно меньше 160 px — заголовок встаёт наверх видимой области
   *  (при «меньше движения» — мгновенно, § 13). */
  const afterExpand = useCallback(
    (header: HTMLElement) => {
      const main = root();
      if (main && spaceBelow(main, header, live.current.dockHeightNow()) < MIN_SPACE_BELOW) {
        scrollToTop(main, header, live.current.reduced);
      }
    },
    [root],
  );

  // --- «Показать ещё» и «Свернуть» у списка (§ 22.7).
  const loadingMore = useRef(false);
  const showMore = async () => {
    if (loadingMore.current) return;
    const next = limit + WINDOW_STEP;
    let loaded = items.length;
    if (items.length < next && query.hasNextPage) {
      loadingMore.current = true;
      setMoreState('loading');
      try {
        // Догрузку может отменить перечитка всего кэша после «Связь восстановлена» — тогда страница ещё раз (A2-11).
        const result = await loadUntil(next, { loaded, hasNext: true, error: false }, async () => {
          const page = await query.fetchNextPage();
          return {
            loaded: listGenerationSummaries(page.data).length,
            hasNext: !!page.hasNextPage,
            error: page.isFetchNextPageError,
          };
        });
        if (result.error) {
          setMoreState('error');
          return;
        }
        loaded = result.loaded;
      } catch {
        setMoreState('error');
        return;
      } finally {
        loadingMore.current = false;
      }
    }
    setMoreState('idle');
    setLimit(next);
    trackEvent('generations_more', { surface, shown: Math.min(next, loaded) });
  };

  const collapseList = () => {
    const keep = new Set(items.slice(0, WINDOW_BASE).map((s) => s.id));
    const run = () => {
      // Лишние записи убираются без анимации, раскрытые за пятёркой сворачиваются.
      setInstantExit(true);
      setLimit(WINDOW_BASE);
      setExpanded((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => keep.has(id))));
    };
    const main = root();
    const heading = headingRef.current;
    if (main && heading && isAboveView(main, heading)) {
      scrollToTop(main, heading, reduced);
      window.setTimeout(run, reduced ? 0 : SCROLL_SETTLE_MS);
    } else run();
  };
  useEffect(() => {
    if (!instantExit) return;
    const timer = window.setTimeout(() => setInstantExit(false), 50);
    return () => window.clearTimeout(timer);
  }, [instantExit]);

  // --- Нажатие сигнала (§ 8): окно расширяется до записи, она раскрывается без анимации высоты, одна
  // прокрутка `main` к заголовку, короткая подсветка и фокус на заголовок. Строки ещё нет — к заголовку
  // секции или в конец главной; запись раскроется, когда появится.
  const revealRequest = useGenerationWatch((s) => s.revealRequest);
  const pendingReveal = useRef<string | null>(null);
  const [revealTarget, setRevealTarget] = useState<string | null>(null);
  const reveal = useCallback(
    (id: string, list: GenerationSummary[]) => {
      const index = list.findIndex((s) => s.id === id);
      if (index < 0) return false;
      const summary = list[index];
      setLimit((current) => Math.max(current, index + 1));
      if (summary.status !== 'removed') setExpanded((prev) => ({ ...prev, [id]: 'instant' }));
      setRevealTarget(id);
      trackEvent('generation_expanded', { surface, source: 'signal', kind: summary.kind, status: summary.status });
      return true;
    },
    [surface],
  );
  useEffect(() => {
    if (!revealRequest) return;
    useGenerationWatch.getState().consumeReveal(revealRequest.at);
    if (reveal(revealRequest.id, items)) return;
    pendingReveal.current = revealRequest.id;
    const main = root();
    if (!main) return;
    if (headingRef.current) scrollToTop(main, headingRef.current, reduced);
    else main.scrollTo({ top: main.scrollHeight, behavior: reduced ? 'auto' : 'smooth' });
    // Только на новый запрос.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealRequest]);
  useEffect(() => {
    const id = pendingReveal.current;
    if (!id) return;
    const index = items.findIndex((s) => s.id === id);
    if (index < 0) return;
    pendingReveal.current = null;
    setLimit((current) => Math.max(current, index + 1));
    if (items[index].status !== 'removed') setExpanded((prev) => ({ ...prev, [id]: 'instant' }));
  }, [items]);
  // Раскрыто без анимации высоты — высота уже окончательная: считаем прокрутку после раскладки. Подсветка — у уже
  // доехавшей записи: после конца плавной прокрутки, а при «меньше движения» и без прокрутки — сразу (A2-08).
  const pendingHighlight = useRef<(() => void) | null>(null);
  useEffect(() => () => pendingHighlight.current?.(), []);
  useLayoutEffect(() => {
    if (!revealTarget) return;
    const id = revealTarget;
    setRevealTarget(null);
    const main = root();
    const header = document.getElementById(headerDomId(id));
    if (!main || !header) return;
    const target = scrollToTop(main, header, reduced);
    header.focus({ preventScroll: true });
    pendingHighlight.current?.();
    pendingHighlight.current = null;
    const light = () => setHighlight({ id, key: Date.now() });
    if (reduced || target === null) light();
    else
      pendingHighlight.current = onScrollSettled(main, target, () => {
        pendingHighlight.current = null;
        light();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealTarget]);
  useEffect(() => {
    if (!highlight) return;
    const timer = window.setTimeout(() => setHighlight(null), 700);
    return () => window.clearTimeout(timer);
  }, [highlight]);

  // --- 404: записи больше нет — уходит (высота и прозрачность до 0), список перечитывается.
  const markGone = useCallback((id: string) => setLeaving((prev) => ({ ...prev, [id]: true })), []);
  const dropGone = useCallback(
    (id: string) => {
      removeGeneration(qc, id);
      setLeaving((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      void qc.invalidateQueries({ queryKey: generationKeys.list() });
    },
    [qc],
  );

  // --- Что рисовать (§ 22.8).
  // Окно захода не показывает ни загрузку, ни ошибку: пока нажатий нет, секции нет.
  const state: 'list' | 'loading' | 'error' | null = hasItems
    ? 'list'
    : visitOnly
      ? null
      : seen && query.isPending
      ? 'loading'
      : seen && query.isError && !query.data
        ? 'error'
        : null;

  const title = (
    <h2 id={titleId} ref={headingRef} className={cn('gl-title', visitOnly && 'gl-title--visit', headerAction && '!mb-0')}>
      {t(visitOnly ? 'generations.titleVisit' : 'generations.title')}
    </h2>
  );
  const heading = headerAction ? (
    <div className="mb-2 flex min-h-[44px] items-center justify-between gap-3">
      {title}
      {headerAction}
    </div>
  ) : (
    title
  );

  let content = null;
  if (state === 'loading') {
    content = (
      <div aria-busy>
        {heading}
        <ul className="gl-list" aria-hidden>
          {[0, 1, 2].map((i) => (
            <li key={i} className="gl-row flex h-16 items-center gap-2 px-3">
              <span className="flex h-6 w-6 flex-none items-center justify-center">
                <span className="h-3.5 w-3.5 animate-skeleton rounded-full bg-elev motion-reduce:animate-none" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-2">
                <Bar width="40%" />
                <Bar width="60%" />
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  } else if (state === 'error') {
    content = (
      <div>
        {heading}
        <div className="flex flex-wrap items-center gap-x-3">
          {/* Не live: секция второстепенная, о связи говорит верхний баннер (§ 22.8, § 22.11). */}
          <p className="text-[14px] leading-5 text-muted">{t('generations.loadError')}</p>
          <button type="button" className="gl-text gl-text--44 gl-text--accent" onClick={() => void query.refetch()}>
            {t('generations.retry')}
          </button>
        </div>
      </div>
    );
  } else if (state === 'list') {
    content = (
      <>
        {heading}
        <ul className="gl-list">
          <AnimatePresence initial={false} custom={instantExit}>
            {visible.map((summary, index) => (
              <GenerationRow
                key={summary.id}
                summary={summary}
                now={now}
                pressedAt={pressed[summary.id] ?? null}
                firstSeenAt={firstSeenAt}
                open={!!expanded[summary.id]}
                instantOpen={expanded[summary.id] === 'instant'}
                highlightKey={highlight?.id === summary.id ? highlight.key : null}
                enter={rowEnter(knownIds.current, summary.id, index, previousLimit.current)}
                leaving={!!leaving[summary.id]}
                reduced={reduced}
                listSettled={listSettled}
                observe={rows.register(summary.id)}
                ctx={ctx}
                onToggle={toggle}
                onCollapseFromBottom={collapseFromBottom}
                onExpanded={afterExpand}
                onGone={markGone}
                onLeft={dropGone}
              />
            ))}
          </AnimatePresence>
        </ul>
        {(canShowMore || canCollapse) &&
          (moreState === 'error' ? (
            <div className="mt-2 flex flex-wrap items-center gap-x-3">
              {/* Ответ на нажатие ученика — объявляется (исключение к § 22.11). */}
              <span role="alert" className="text-[14px] leading-5 text-muted">
                {t('generations.moreError')}
              </span>
              <button type="button" className="gl-text gl-text--44 gl-text--accent" onClick={() => void showMore()}>
                {t('generations.retry')}
              </button>
            </div>
          ) : (
            // Одна кнопка: после «Показать ещё» фокус остаётся на ней, а когда показывать больше нечего,
            // она становится «Свернуть» (§ 22.11).
            <button
              type="button"
              className="gl-text gl-more relative"
              // Не disabled: заблокированная кнопка теряет фокус, а после «Показать ещё» он остаётся на ней.
              onClick={() => {
                if (moreState === 'loading') return;
                if (canShowMore) void showMore();
                else collapseList();
              }}
              aria-disabled={moreState === 'loading' || undefined}
              aria-busy={moreState === 'loading' || undefined}
            >
              <span className={cn('inline-flex items-center gap-1.5', moreState === 'loading' && 'invisible')}>
                {canShowMore ? t('generations.more') : t('generations.less')}
                <ChevronDownIcon className={cn('h-3.5 w-3.5', !canShowMore && 'rotate-180')} />
              </span>
              {moreState === 'loading' && (
                <span className="absolute inset-0 flex items-center gap-1.5 whitespace-nowrap px-1">
                  <Spinner className="h-3.5 w-3.5" />
                  {t('generations.loading')}
                </span>
              )}
            </button>
          ))}
      </>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <AnimatePresence initial={false}>
        {content && (
          // Первая запись приходит вместе с заголовком: секция целиком — высота и прозрачность, 260 мс.
          // Блоки выше секции не двигаются: она растёт только вниз.
          <SectionShell key="generations" titleId={titleId} reduced={reduced} visit={visitOnly}>
            {content}
          </SectionShell>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
}

function SectionShell({
  titleId,
  reduced,
  visit,
  children,
}: {
  titleId: string;
  reduced: boolean;
  visit: boolean;
  children: ReactNode;
}) {
  // overflow: hidden — только пока едет высота: иначе обрезались бы рамки фокуса у краёв.
  const [moving, setMoving] = useState(false);
  return (
    <motion.section
      aria-labelledby={titleId}
      className={cn('gl-section', visit && 'gl-section--visit')}
      initial={reduced ? false : { height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1, transition: { duration: reduced ? 0 : 0.26, ease: [0.2, 0.9, 0.2, 1] } }}
      exit={{ height: 0, opacity: 0, transition: { duration: reduced ? 0 : 0.2 } }}
      onAnimationStart={() => setMoving(true)}
      onAnimationComplete={() => setMoving(false)}
      style={{ overflow: moving ? 'hidden' : undefined }}
    >
      {children}
    </motion.section>
  );
}
